import React, { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import logoImage from './assets/logo.png';
import { FaCalendarAlt, FaRocket } from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';

import { AuthProvider, UIProvider, LocationProvider } from './context';
import { GlobalToast } from './components/common/GlobalToast';
import PageLoadingFallback from './components/common/PageLoadingFallback';
import ProtectedRoute from './components/common/ProtectedRoute';
import { getVendorSession } from './utils/vendorSession';
import { getVendorDashboardPath } from './utils/vendorDashboard';
import './App.css';

// ── Lazy-Loaded Pages & Heavy Components (React.lazy + Suspense) ──
const HomePage = lazy(() => import('./pages/HomePage'));
const CustomerPage = lazy(() => import('./pages/CustomerPage'));
const VendorPage = lazy(() => import('./pages/VendorPage'));
const VendorSignupPage = lazy(() => import('./pages/VendorSignupPage'));
const VendorDashboardPage = lazy(() => import('./pages/VendorDashboardPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const TravelPage = lazy(() => import('./pages/TravelPage'));
const EatDrinkPage = lazy(() => import('./pages/EatDrinkPage'));
const ShoppingPage = lazy(() => import('./pages/ShoppingPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const NeighbourhoodPage = lazy(() => import('./pages/NeighbourhoodPage'));
const NeighbourhoodCityPage = lazy(() => import('./pages/NeighbourhoodCityPage'));
const TempleListingPage = lazy(() => import('./features/temples/pages/TempleListingPage'));
const TempleDetailsPage = lazy(() => import('./features/temples/pages/TempleDetailsPage'));
const RestaurantMenuPage = lazy(() => import('./features/eat-drink/pages/RestaurantMenuPage'));
const CheckoutPage = lazy(() => import('./features/eat-drink/checkout/pages/CheckoutPage'));
const OrderConfirmationPage = lazy(() => import('./features/eat-drink/checkout/pages/OrderConfirmationPage'));
const RideBookingPage = lazy(() => import('./views/pages/RideBookingPage'));
const OutstationPage = lazy(() => import('./pages/OutstationPage'));
const FlightBookingPage = lazy(() => import('./pages/FlightBookingPage'));
const BusBookingPage = lazy(() => import('./pages/BusBookingPage'));
const HotelBookingPage = lazy(() => import('./pages/HotelBookingPage'));
const UniqueStaysPage = lazy(() => import('./pages/UniqueStaysPage'));
const UniqueStayBookingPage = lazy(() => import('./pages/UniqueStayBookingPage'));
const AdventuresPage = lazy(() => import('./pages/AdventuresPage'));
const SmartTravelResultsPage = lazy(() => import('./pages/SmartTravelResultsPage'));
const DestinationsPage = lazy(() => import('./pages/DestinationsPage'));
const DestinationPage = lazy(() => import('./pages/DestinationPage'));
const SrisailamThingsToDoPage = lazy(() => import('./pages/SrisailamThingsToDoPage'));
const TirupatiThingsToDoPage = lazy(() => import('./pages/TirupatiThingsToDoPage'));
const AhobilamThingsToDoPage = lazy(() => import('./pages/AhobilamThingsToDoPage'));
const LepakshiThingsToDoPage = lazy(() => import('./pages/LepakshiThingsToDoPage'));
const ArakuThingsToDoPage = lazy(() => import('./pages/ArakuThingsToDoPage'));
const PapikondaluThingsToDoPage = lazy(() => import('./pages/PapikondaluThingsToDoPage'));
const LambasingiThingsToDoPage = lazy(() => import('./pages/LambasingiThingsToDoPage'));
const BorraCavesDetailsPage = lazy(() => import('./pages/BorraCavesDetailsPage'));
const KanakaDurgaThingsToDoPage = lazy(() => import('./pages/KanakaDurgaThingsToDoPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DashboardBookingsPage = lazy(() => import('./pages/DashboardBookingsPage'));
const DashboardSavedPage = lazy(() => import('./pages/DashboardSavedPage'));
const DashboardSettingsPage = lazy(() => import('./pages/DashboardSettingsPage'));
const DashboardRidesPage = lazy(() => import('./pages/DashboardRidesPage'));
const DashboardTicketPage = lazy(() => import('./pages/DashboardTicketPage'));
const DashboardEatDrinkOrdersPage = lazy(() => import('./pages/DashboardEatDrinkOrdersPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));

function ScrollToHash() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const scrollToTarget = () => {
      const target = document.querySelector(location.hash);
      if (!target) return;

      const headerOffset = 90;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: targetTop, behavior: 'smooth' });
    };

    const timer = setTimeout(scrollToTarget, 80);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  return null;
}

function HomeRoute() {
  const vendorSession = getVendorSession();

  if (vendorSession?.vendor) {
    return <Navigate to={getVendorDashboardPath(vendorSession.vendor)} replace />;
  }

  return <HomePage />;
}

const IS_MAINTENANCE = false;

function App() {
  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 80, easing: 'ease-out-cubic' });
  }, []);

  if (IS_MAINTENANCE) {
    return (
      <div className="maintenance-screen">
        <div className="maintenance-bg-grid" />
        <div className="maintenance-glow-1" />
        <div className="maintenance-glow-2" />
        <div className="maintenance-glow-3" />

        <div className="maintenance-card">
          <div className="maintenance-logo-container">
            <img className="maintenance-logo" src={logoImage} alt="Andhra Yatri Logo" />
          </div>

          <div className="maintenance-badge-container">
            <span className="maintenance-badge-dot" />
            <span className="maintenance-badge-text">Launch Announcement</span>
          </div>

          <h1 className="maintenance-title">A New Era of Travel is Coming</h1>
          <p className="maintenance-subtitle">
            We are preparing the ultimate travel and operations platform for Andhra Pradesh. Our services are getting ready for deployment.
          </p>

          <div className="roadmap-container">
            <div className="roadmap-card phase-beta">
              <div className="roadmap-icon-wrapper">
                <FaRocket />
              </div>
              <div>
                <span className="roadmap-phase-num">Phase 01</span>
                <h3 className="roadmap-phase-title">Beta & Early Access</h3>
                <p className="roadmap-phase-desc">Explore preliminary travel guides, unique stays booking, and early testing features.</p>
              </div>
              <div className="roadmap-date-badge">
                12 JULY 2026
              </div>
            </div>

            <div className="roadmap-card phase-regular">
              <div className="roadmap-icon-wrapper">
                <FaCalendarAlt />
              </div>
              <div>
                <span className="roadmap-phase-num">Phase 02</span>
                <h3 className="roadmap-phase-title">Regular App Launch</h3>
                <p className="roadmap-phase-desc">Complete passenger transport, flight/bus bookings, local activities, and administrative suite.</p>
              </div>
              <div className="roadmap-date-badge">
                15 AUGUST 2026
              </div>
            </div>
          </div>

          <div className="maintenance-footer">
            &copy; {new Date().getFullYear()} Andhra Yatri. All rights reserved.
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <UIProvider>
        <GlobalToast />
        <LocationProvider>
          <div className="app">
            <ScrollToHash />
            <Suspense fallback={<PageLoadingFallback />}>
              <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/login" element={<AuthPage />} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/customer" element={<CustomerPage />} />
                <Route path="/customer-sign-in" element={<CustomerPage />} />
                <Route path="/customer-sign-up" element={<CustomerPage />} />
                <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/dashboard/bookings" element={<ProtectedRoute><DashboardBookingsPage /></ProtectedRoute>} />
                <Route path="/dashboard/saved" element={<ProtectedRoute><DashboardSavedPage /></ProtectedRoute>} />
                <Route path="/dashboard/settings" element={<ProtectedRoute><DashboardSettingsPage /></ProtectedRoute>} />
                <Route path="/dashboard/rides" element={<ProtectedRoute><DashboardRidesPage /></ProtectedRoute>} />
                <Route path="/dashboard/ticket/:bookingId" element={<ProtectedRoute><DashboardTicketPage /></ProtectedRoute>} />
                <Route path="/dashboard/eat-drink-orders" element={<ProtectedRoute><DashboardEatDrinkOrdersPage /></ProtectedRoute>} />
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/vendor" element={<VendorPage />} />
                <Route path="/vendor-signup" element={<VendorSignupPage />} />
                <Route path="/vendor-dashboard" element={<VendorDashboardPage />} />
                <Route path="/vendor-dashboard/:category" element={<VendorDashboardPage />} />
                <Route path="/rides" element={<ProtectedRoute><RideBookingPage /></ProtectedRoute>} />
                <Route path="/ride" element={<ProtectedRoute><RideBookingPage /></ProtectedRoute>} />
                <Route path="/cabs" element={<ProtectedRoute><RideBookingPage /></ProtectedRoute>} />
                <Route path="/outstation" element={<ProtectedRoute><OutstationPage /></ProtectedRoute>} />
                <Route path="/outstation-cabs" element={<ProtectedRoute><OutstationPage /></ProtectedRoute>} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/bus-booking" element={<ProtectedRoute><BusBookingPage /></ProtectedRoute>} />
                <Route path="/flight-booking" element={<ProtectedRoute><FlightBookingPage /></ProtectedRoute>} />
                <Route path="/hotel-booking" element={<ProtectedRoute><HotelBookingPage /></ProtectedRoute>} />
                <Route path="/unique-stays" element={<ProtectedRoute><UniqueStaysPage /></ProtectedRoute>} />
                <Route path="/unique-stays/book/:stayId" element={<ProtectedRoute><UniqueStayBookingPage /></ProtectedRoute>} />
                <Route path="/adventures" element={<ProtectedRoute><AdventuresPage /></ProtectedRoute>} />
                <Route path="/smart-travel/:sectionKey" element={<SmartTravelResultsPage />} />
                <Route path="/smart-travel/:placeId/:sectionKey" element={<SmartTravelResultsPage />} />
                <Route path="/destinations" element={<DestinationsPage />} />
                <Route path="/destinations/:slug" element={<DestinationPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/things-to-do" element={<Navigate to="/" replace />} />
                <Route path="/things-to-do/srisailam" element={<SrisailamThingsToDoPage />} />
                <Route path="/things-to-do/tirupati" element={<TirupatiThingsToDoPage />} />
                <Route path="/things-to-do/ahobilam" element={<AhobilamThingsToDoPage />} />
                <Route path="/things-to-do/lepakshi" element={<LepakshiThingsToDoPage />} />
                <Route path="/things-to-do/araku-valley" element={<ArakuThingsToDoPage />} />
                <Route path="/things-to-do/papikondalu" element={<PapikondaluThingsToDoPage />} />
                <Route path="/things-to-do/lambasingi" element={<LambasingiThingsToDoPage />} />
                <Route path="/things-to-do/borra-caves" element={<BorraCavesDetailsPage />} />
                <Route path="/things-to-do/kanaka-durga-temple" element={<KanakaDurgaThingsToDoPage />} />
                <Route path="/things-to-do/vijayawada" element={<KanakaDurgaThingsToDoPage />} />
                <Route path="/things-to-do/:slug" element={<DestinationPage />} />
                {/* Navigation routes */}
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/neighbourhoods" element={<NeighbourhoodPage />} />
                <Route path="/neighbourhoods/:citySlug" element={<NeighbourhoodCityPage />} />
                <Route path="/travel" element={<TravelPage />} />
                <Route path="/eat-drink" element={<EatDrinkPage />} />
                <Route path="/eat-drink/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
                <Route path="/eat-drink/order/:orderId" element={<ProtectedRoute><OrderConfirmationPage /></ProtectedRoute>} />
                <Route path="/eat-drink/:id" element={<RestaurantMenuPage />} />
                <Route path="/temples" element={<TempleListingPage />} />
                <Route path="/temples/:id" element={<TempleDetailsPage />} />
                <Route path="/shopping" element={<ShoppingPage />} />
                <Route path="/contact" element={<ContactPage />} />
              </Routes>
            </Suspense>
          </div>
        </LocationProvider>
      </UIProvider>
    </AuthProvider>
  );
}

export default App;
